<script lang="ts" setup>
import { ButtonStyle } from '~/static/enums/components/button'
import AButton from '~/components/atoms/buttons/Button.vue'
import RModal from '~/components/organs/Modal.vue'
import ABaseInput from '~/components/atoms/forms/BaseInput.vue'
import { InputType } from '~/static/enums/components/base-input'
import { useLogin } from '~/store/login'
import { storeToRefs } from 'pinia'
import AIconYandex from '~/components/atoms/icons/Yandex.vue'
import { track } from '~/utils/Amplitude'
import { OAuthErrorCode } from '~/static/enums/error-code'
import { PopupType } from '~/static/enums/popup'
import { BIG_DESKTOP_WIDTH } from '~/static/window-sizing'

interface Props {
  title: string
}

defineProps<Props>()
const emit = defineEmits<{
  (e: 'authorized'): void
}>()

const loginStore = useLogin()
const { email, mailSent, emailError, code, codeError, lock_expiration_time } = storeToRefs(loginStore)

loginStore.$reset()

const showYaAuthButton = ref(true)

const { $event } = useNuxtApp()

onMounted(() => {
  if (window.innerWidth >= BIG_DESKTOP_WIDTH) {
    setFocus()
  }
  let yandexScript = document.createElement('script')
  // eslint-disable-next-line max-len
  yandexScript.setAttribute('src', 'https://yastatic.net/s3/passport-sdk/autofill/v1/sdk-suggest-with-polyfills-latest.js')
  document.head.appendChild(yandexScript)
  yandexScript.addEventListener('load', async () => {
    initSuggests('ya-login')
  })
})

const initSuggests = (buttonId: string) => {
  if (
    'YaAuthSuggest' in window &&
    typeof window.YaAuthSuggest === 'object' &&
    window.YaAuthSuggest !== null &&
    'init' in window.YaAuthSuggest &&
    typeof window.YaAuthSuggest.init === 'function'
  ) {
    const config = useRuntimeConfig().public
    window.YaAuthSuggest.init(
      {
        client_id: config.yandexOAuthClientId,
        response_type: 'token',
        redirect_uri: config.appUrl + '/oauth',
      },
      config.appUrl,
      {
        view: 'button',
        parentId: buttonId,
        buttonSize: 's',
        buttonView: 'additional',
        buttonTheme: 'light',
        buttonBorderRadius: '4',
        buttonIcon: 'ya',
      }
    )
      .then(({ handler }: { handler: () => Promise<void> }) => {
        track('Auth Yandex ID')
        return handler()
      })
      .then(async (data: { access_token: string, cid: string, expires_in: string, token_type: string }) => {
        const result = await loginStore.loginByService(data.access_token)
        if (result === OAuthErrorCode.InappropriateAccount) {
          track('Auth Yandex ID fail')
          useNuxtApp().$event('popup:show', {
            text: 'Не получилось войти через Яндекс ID. Попробуйте войти по эл. почте',
          })
        } else if (result !== true) {
          track('Auth Yandex ID fail')
          useNuxtApp().$event('popup:show', {
            text: 'Не получилось войти через Яндекс ID. Попробуйте еще раз или войдите по эл. почте',
          })
        } else {
          track('Auth Yandex ID success')
        }
        closeModal()
        return console.log(data)
      })
      .catch((error: Error) => {
        showYaAuthButton.value = false
        track('Auth Yandex ID fail')
        return console.log(error)
      })
  } else {
    showYaAuthButton.value = false
  }
}

const emailInputRef: Ref = ref(null)
const codeInputRef: Ref = ref(null)
const setFocus = () => {
  if (emailInputRef.value !== null) {
    emailInputRef.value.setFocus()
  } else if (codeInputRef.value !== null) {
    codeInputRef.value.setFocus()
  }
}

const changeEmail = (value: string): void => {
  loginStore.email = value
  loginStore.emailError = ''
}

const sendCode = (): void => {
  loginStore.mailSent = true
  loginStore.lock_expiration_time = Math.round(Date.now() / 1000) + 60
  nextTick(() => {
    setFocus()
    loginStore.sendCode().catch(() => {
      $event('popup:show', {
        type: PopupType.Warning,
        text: 'Произошла ошибка, обратитесь в службу поддержки',
      })
    })
  })
}

const changeCode = (value: string): void => {
  loginStore.code = value
  loginStore.codeError = ''
  if (loginStore.code.length >= 4) {
    checkCode()
  }
}

const checkCode = async () => {
  if (await loginStore.checkCode()) {
    closeModal()
    loginStore.$reset()
    emit('authorized')
  }
}

const codeNotCome = false

const canResend = computed<boolean>(() => {
  return time.value <= 0
})

const time = ref<number>(0)
const interval = ref<NodeJS.Timeout | null>(null)
watch(() => {
  return lock_expiration_time.value
}, () => {
  time.value = lock_expiration_time.value * 1000 - Date.now()
  interval.value = setInterval(() => {
    time.value = lock_expiration_time.value * 1000 - Date.now()
    if (canResend.value) {
      clearInterval(<NodeJS.Timeout>interval.value)
      interval.value = null
    }
  }, 1000)
})

const displayedTime = computed<string>((): string => {
  let result = ''
  if (!canResend.value) {
    const timestamp = Math.trunc(time.value / 1000)
    const minutes = Math.trunc(timestamp / 60)
    const seconds = timestamp % 60
    result += (minutes + ':')
    if (seconds < 10) {
      result += '0'
    }
    result += seconds
  }
  return result
})

const changeAccount = (): void => {
  loginStore.mailSent = false
  nextTick(() => {
    initSuggests('ya-login')
  })
}

const refModal: Ref = ref(null)
const closeModal = () => {
  if (refModal.value !== null) {
    refModal.value.closeModal()
  }
}
defineExpose({
  closeModal,
})

</script>

<template>
  <RModal
    ref="refModal"
    name-modal="SignIn"
  >
    <template #header>
      <h5
        class="modal-title fs-5"
        id="signInModalLabel"
      >
        {{ title }}
      </h5>
    </template>

    <template #body>
      <template v-if="!mailSent">
        <label
          for="FormControlInputMail"
          class="form-label text-dark"
        >
          Введите электронную почту, чтобы получить код для входа
        </label>
        <ABaseInput
          ref="emailInputRef"
          :value="email"
          :type="InputType.Text"
          placeholder="mail@mail.ru"
          class="mb-1"
          :error-message="emailError"
          @change="changeEmail"
          @input="changeEmail"
          @enter="changeEmail"
        />
        <span class="small">Нажимая кнопку «Получить код», вы соглашаетесь</span>
        <a
          href="/legal/privacy-policy.pdf"
          target="_blank"
          class="personal-data-link"
        > с обработкой персональных данных</a>
      </template>
      <template v-else>
        <label
          for="FormControlInputCode"
          class="form-label"
        >
          <span class="text-dark">Код отправлен на эл. почту:</span><br>
          <div class="d-flex align-items-center">
            <div class="mail-wrap">
              <span class="text-dark">{{ email }}</span>&nbsp;
            </div>
            <a
              href="#"
              class="link"
              @click.prevent="changeAccount()"
            >Изменить</a>
          </div>

        </label>
        <ABaseInput
          ref="codeInputRef"
          :value="code"
          :type="InputType.Number"
          inputmode="numeric"
          placeholder="Код"
          class="mb-1"
          :error-message="codeError"
          @change="changeCode"
          @input="changeCode"
          @enter="changeCode"
        />
        <div class="form-text text-dark">
          Если письмо с кодом не пришло, проверьте папку «Спам»
        </div>

        <div
          :class="{ 'd-none': !codeNotCome }"
          class="mt-3"
        >
          Напишите в поддержку, если не приходит код.
        </div>
        <a
          v-if="codeNotCome"
          href="#"
          class="link"
        >Написать в поддержку</a>

        <div
          v-if="!codeNotCome"
          class="mt-3"
        >
          <span v-if="!canResend">Отправить код повторно через {{ displayedTime }}</span>
          <a
            v-else
            href="#"
            class="link"
            @click.prevent="sendCode()"
          >Отправить код повторно</a>
        </div>
      </template>
    </template>
    <template
      #footer
      v-if="!mailSent"
    >
      <div class="btns-wrapper d-flex flex-column">
        <AButton
          :button-style="ButtonStyle.Primary"
          :disabled="email === ''"
          @click="sendCode()"
        >
          Получить код
        </AButton>
        <div
          v-if="!mailSent && showYaAuthButton"
          class="btn-yandex-wrap"
        >
          <div class="d-flex align-items-center justify-content-center text-or my-1">
            или
          </div>
          <button
            class="btn-yandex d-flex align-items-center justify-content-center"
            id="ya-login"
          >
            <AIconYandex class="icon" /> Войти с Яндекс ID
          </button>
        </div>
      </div>
    </template>
  </RModal>
</template>

<style lang="scss" scoped>
.link {
  color: $indigo;
  text-decoration: none;
}

.mail-wrap {
  max-width: rem(380px);
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  @include media-breakpoint-down(sm) {
    max-width: rem(230px);
  }
}

.personal-data-link {
  display: block;
  font-size: rem(14px);
  text-decoration: underline;
  margin-bottom: rem(48px);
  transition: all .2s ease-in-out;
  color: $dark;

  @include media-breakpoint-down(lg) {
    margin-bottom: 0;
  }

  @media (any-hover: hover) {
    &:hover {
      color: $indigo-500;
    }
  }
}

.btns-wrapper {
  @include media-breakpoint-down(lg) {
    width: rem(208px);
  }

  @include media-breakpoint-down(md) {
    width: 100%;
  }
}

.text-or {
  color: $white;

  @include media-breakpoint-down(lg) {
    color: $gray-900;
  }
}

.btn-yandex {
  padding: 0;
  border: none;
  background: $black;
  color: $white;
  border-radius: rem(4px);
  width: 100%;
  height: rem(40px);
  font-size: rem(14px);
  line-height: 150%;
  overflow: hidden;

  @include media-breakpoint-up(lg) {
    min-width: 483px;
    max-width: 483px;
    background: $white;
    color: $black;
  }

  @include media-breakpoint-down(md) {
    width: calc(100% - 8px);
  }

  .icon {
    margin-right: rem(14px);
  }
}

.btn-yandex-wrap {
  @include media-breakpoint-up(lg) {
    position: absolute;
    bottom: -72px;
    left: 0;
  }
}

:deep(.modal-dialog) {
  @include media-breakpoint-up(lg) {
    padding: rem(40px) 0 rem(80px);
  }
}
</style>
